import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import AuthModal from '../components/auth/AuthModal';
import Logo from '../components/ui/Logo';
import useDarkMode from '../hooks/useDarkMode';
import '../styles/landing.css';

// ─────────────────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────────────────

const THEME_CONFIG = {
  'zero-friction':     { label: 'Zero-friction logging', icon: '⚡', color: '#FF85C0' },
  'caregiver-sync':    { label: 'Caregiver sync',        icon: '🤝', color: '#4DD0E1' },
  'sleep-guidance':    { label: 'Sleep intelligence',    icon: '🌙', color: '#CE93D8' },
  'proactive':         { label: 'Proactive guidance',    icon: '💡', color: '#FFD54F' },
  'health':            { label: 'Health & medical',       icon: '🏥', color: '#A5D6A7' },
  'privacy':           { label: 'Privacy & security',    icon: '🔒', color: '#80CBC4' },
};

const HORIZONS = [
  {
    id:    'now',
    label: 'Now',
    sub:   'Making the core experience reliable and frictionless enough for wider use.',
    colour: '#dcfce7',
    text:   '#166534',
    items: [
      { theme: 'zero-friction',  icon: '⏱️', title: 'Last logged time',       body: '"Last feed: 2h 30m ago" on the dashboard. The question caregivers actually have at 3am, answered in one glance.' },
      { theme: 'zero-friction',  icon: '👋', title: 'Swipe to delete',         body: 'Swipe left on any table row to delete an entry on mobile. No hunting for a small button.' },
      { theme: 'zero-friction',  icon: '💬', title: 'Better empty states',     body: 'Every section gives a clear prompt when nothing is logged yet. No blank tables — just a simple nudge to log the first entry.' },
      { theme: 'caregiver-sync', icon: '✅', title: 'Read receipts',           body: 'See who has already seen each activity feed entry. No more "did you see that?" — the feed shows it right on the item.' },
      { theme: 'caregiver-sync', icon: '🟢', title: 'Co-parent last active',   body: 'See when your co-parent last opened the app. Answers "do they know?" without needing to send a message.' },
      { theme: 'proactive',      icon: '📊', title: 'At-a-glance strip',       body: 'A compact row showing the last log time for each active section. Replaces totals with what caregivers actually scan for.' },
      { theme: 'proactive',      icon: '📈', title: 'Weekly comparison',       body: 'This week vs last week for feeds, sleep, and nappies — with trend arrows. One number per metric, instantly readable.' },
      { theme: 'proactive',      icon: '🎯', title: 'Daily goal progress',     body: 'Set a target (e.g. 6 bottles/day) and track progress on the dashboard. A simple bar that fills through the day.' },
      { theme: 'zero-friction',  icon: '📍', title: 'Pinnable shortcuts',      body: 'Each caregiver can pin their 2–3 most-used log buttons to the top of the dashboard.' },
    ],
  },
  {
    id:    'next',
    label: 'Next',
    sub:   'Expanding the app\'s value and building toward a wider release.',
    colour: '#fef3c7',
    text:   '#92400e',
    items: [
      { theme: 'proactive',      icon: '🎯', title: 'Developmental activities', body: 'Weekly age-appropriate suggestions for play and stimulation, surfaced on the dashboard. Turns the app from a logger into a companion.' },
      { theme: 'health',         icon: '💊', title: 'Medication reminders',     body: 'Log a medication and set a follow-up reminder. Flags automatically if a second dose is logged too soon — a real safety feature.' },
      { theme: 'health',         icon: '📋', title: 'Doctor visit summary',     body: 'A structured PDF export designed to be handed directly to a health visitor or paediatrician — not a data dump, a clinical summary.' },
      { theme: 'privacy',        icon: '🔒', title: 'Data portability',         body: 'Export everything as a single JSON file. Delete your account and all associated data in one step. Your data, your control.' },
    ],
  },
  {
    id:    'later',
    label: 'Later',
    sub:   'Broader audience, deeper retention, stronger differentiation.',
    colour: 'var(--bg-light)',
    text:   'var(--text-light)',
    items: [
      { theme: 'zero-friction',  icon: '⏱️', title: 'Contraction timer',           body: 'For parents tracking labour — reuses the existing timer infrastructure. High acquisition value: parents use it before the baby arrives.' },
      { theme: 'health',         icon: '📈', title: 'Growth percentile alerts',    body: 'Proactive notification when measurements shift between percentile bands. Flags something worth raising with a health visitor.' },
      { theme: 'proactive',      icon: '📅', title: 'Routine & schedule builder',  body: 'Define a target daily routine and compare actual logs to it. Popular in sleep-training communities.' },
      { theme: 'zero-friction',  icon: '🍼', title: 'Breast pump & milk inventory', body: 'Log pumping sessions and track stored milk volume. The most underserved segment in existing baby tracking apps.' },
      { theme: 'health',         icon: '❤️', title: 'Health platform export',      body: 'Export data in a format compatible with Apple Health or Google Fit. Complex on a PWA — JSON in CDA format is the practical first step.' },
    ],
  },
];

const SHIPPED = [
  { icon: '📅', title: '24-hour timeline view',         date: 'Apr 2026', body: 'A visual day view showing all events on a time axis — sleep bars for naps and night sleep, plus dots for every feed, nappy, and more.' },
  { icon: '👤', title: 'Logged by',                     date: 'Apr 2026', body: 'Every entry in section tables shows which caregiver logged it. Matters when handing over between a partner, grandparent, or nanny.' },
  { icon: '📷', title: 'Photo attachments on journal',  date: 'Apr 2026', body: 'Add a photo to any journal entry, stored securely. The journal becomes a real baby book, not just a log.' },
  { icon: '👶', title: 'First-child setup flow',        date: 'Apr 2026', body: 'New parents are guided through adding a child profile before hitting the dashboard — no more landing on an empty screen.' },
  { icon: '🎯', title: 'Bedtime consistency score',     date: 'Apr 2026', body: 'Shows how consistent bedtime is across the past two weeks. High consistency = good routine — useful for sleep training.' },
  { icon: '😴', title: 'Sleep debt indicator',          date: 'Apr 2026', body: 'Compare today\'s total sleep to the age-appropriate target. Know at a glance if your baby is behind on sleep.' },
  { icon: '🌞', title: 'Nap count at a glance',         date: 'Apr 2026', body: '"2 naps today · last ended 1h 20m ago" — surfaced on the dashboard without opening the sleep section.' },
  { icon: '⚡', title: 'Quick-log FAB',                 date: 'Apr 2026', body: 'A floating button on every page. Log the most common events in one tap — no navigating, no form hunting.' },
  { icon: '🔁', title: 'Same as last on bottle feeds',  date: 'Apr 2026', body: 'Bottle feed forms pre-fill with the previous entry\'s volume and type. Most feeds are identical — saves three taps every time.' },
  { icon: '🧷', title: 'One-tap nappy log',             date: 'Apr 2026', body: 'Wet, dirty, or mixed as three buttons in the quick-log sheet. No form, no fields — log and done.' },
  { icon: '💡', title: 'Smart pattern alerts',          date: 'Apr 2026', body: 'Rule-based insight cards on the dashboard using 7 days of data — feeding intervals, sleep trends, and nappy patterns.' },
  { icon: '⏱️', title: 'Wake window recommendations',  date: 'Apr 2026', body: 'Live awake timer with age-based guidance, bedtime averaging from real sleep history, and amber warning state when overdue.' },
  { icon: '🔒', title: 'Privacy & Terms pages',         date: 'Apr 2026', body: 'Full UK GDPR-compliant privacy policy and terms of service — required before any public launch.' },
];

const PRINCIPLES = [
  { icon: '🎯', text: 'Reliability first — a feature that works 100% of the time is worth more than three that work 80% of the time.' },
  { icon: '🚫', text: 'No ads. No data selling. Ever.' },
  { icon: '👨‍👩‍👧', text: 'Built for caregivers, not just parents — mums, dads, grandparents, nannies, partners.' },
  { icon: '🔒', text: 'Privacy by default — we collect only what is needed to make the app work.' },
];

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function ThemeBadge({ themeId }) {
  const t = THEME_CONFIG[themeId];
  if (!t) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
      padding: '0.15rem 0.5rem', borderRadius: '50px',
      fontSize: '0.7rem', fontWeight: 600,
      background: t.color + '30', color: t.color,
      border: `1px solid ${t.color}60`,
    }}>
      {t.icon} {t.label}
    </span>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: 'var(--bg-white)', border: '1px solid var(--border-light)',
      borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.82rem',
    }}>
      <strong>{d.icon} {d.label}</strong>
      <div style={{ color: 'var(--text-light)' }}>{d.count} item{d.count !== 1 ? 's' : ''}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function RoadmapPage() {
  const navigate                      = useNavigate();
  const { isDark, toggleDark }        = useDarkMode();
  const [authOpen, setAuthOpen]       = useState(false);
  const [authMode, setAuthMode]       = useState('signup');
  const [activeHorizon, setActiveHorizon] = useState('now');
  const [activeTheme, setActiveTheme]     = useState('all');

  const openLogin  = () => { setAuthMode('login');  setAuthOpen(true); };
  const openSignup = () => { setAuthMode('signup'); setAuthOpen(true); };

  const horizon = HORIZONS.find((h) => h.id === activeHorizon);
  const filteredItems = activeTheme === 'all'
    ? horizon.items
    : horizon.items.filter((i) => i.theme === activeTheme);

  // Chart data: item count by theme for the active horizon
  const chartData = Object.entries(THEME_CONFIG)
    .map(([id, t]) => ({
      id,
      label: t.label,
      icon:  t.icon,
      color: t.color,
      count: horizon.items.filter((i) => i.theme === id).length,
    }))
    .filter((d) => d.count > 0)
    .sort((a, b) => b.count - a.count);

  // Total counts for the stats strip
  const nowCount  = HORIZONS.find((h) => h.id === 'now').items.length;
  const totalItems = HORIZONS.reduce((s, h) => s + h.items.length, 0);

  return (
    <div className="landing-root">

      {/* Nav */}
      <nav className="landing-nav">
        <a href="/"><Logo variant="full" size={32} /></a>
        <div className="landing-nav-actions">
          <button className="btn btn-sm landing-nav-theme" onClick={toggleDark} aria-label="Toggle dark mode">
            {isDark ? '☀️' : '🌙'}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={openLogin}>Log in</button>
          <button className="btn btn-sm btn-primary"            onClick={openSignup}>Sign up free</button>
        </div>
      </nav>

      {/* Page header */}
      <div className="docs-page-header">
        <div className="landing-container">
          <h1 className="docs-page-title">Product Roadmap</h1>
          <p className="docs-page-sub">
            Where Baby Tracker is headed — and why. We use Now / Next / Later instead of
            dates because priorities shift as we learn from real families.
          </p>
        </div>
      </div>

      <div className="landing-container" style={{ paddingTop: '2rem', paddingBottom: '4rem' }}>

        {/* Stats strip */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: '1rem',
          marginBottom: '2.5rem',
        }}>
          {[
            { value: '14+',        label: 'Tracking categories today' },
            { value: nowCount,     label: 'Improvements in progress' },
            { value: totalItems,   label: 'Planned features total' },
            { value: '6',          label: 'Strategic themes' },
          ].map((s) => (
            <div key={s.label} style={{
              background: 'var(--bg-white)', border: '1px solid var(--border-light)',
              borderRadius: '10px', padding: '1rem',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--primary-dark)' }}>
                {s.value}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-light)', lineHeight: 1.3 }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Vision */}
        <section style={{ marginBottom: '2.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-dark)', marginBottom: '0.75rem' }}>
            Vision
          </h2>
          <blockquote style={{
            borderLeft: '4px solid var(--primary-dark)',
            paddingLeft: '1.25rem',
            margin: 0,
            fontSize: '1.05rem',
            fontStyle: 'italic',
            color: 'var(--text-dark)',
            lineHeight: 1.6,
          }}>
            The most trusted, frictionless app for families to stay in sync around a child — at any
            age, across any number of caregivers — without ads, without selling data, and without friction.
          </blockquote>
        </section>

        {/* Horizon tabs */}
        <section style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            {HORIZONS.map((h) => (
              <button
                key={h.id}
                onClick={() => { setActiveHorizon(h.id); setActiveTheme('all'); }}
                style={{
                  padding: '0.4rem 1.1rem',
                  borderRadius: '50px',
                  border: activeHorizon === h.id ? 'none' : '1px solid var(--border-light)',
                  background: activeHorizon === h.id ? h.colour : 'var(--bg-white)',
                  color: activeHorizon === h.id ? h.text : 'var(--text-light)',
                  fontWeight: activeHorizon === h.id ? 700 : 400,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                }}
              >
                {h.label}
                <span style={{
                  background: activeHorizon === h.id ? h.text + '20' : 'var(--bg-light)',
                  color: activeHorizon === h.id ? h.text : 'var(--text-light)',
                  borderRadius: '50px',
                  padding: '0 0.4rem',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                }}>
                  {h.items.length}
                </span>
              </button>
            ))}
          </div>
          <p style={{ color: 'var(--text-light)', fontSize: '0.875rem', margin: 0 }}>
            {horizon.sub}
          </p>
        </section>

        {/* Theme filter chips */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <button
            onClick={() => setActiveTheme('all')}
            style={{
              padding: '0.25rem 0.75rem', borderRadius: '50px', fontSize: '0.78rem',
              border: '1px solid var(--border-light)', cursor: 'pointer',
              background: activeTheme === 'all' ? 'var(--primary-dark)' : 'var(--bg-white)',
              color: activeTheme === 'all' ? '#fff' : 'var(--text-dark)',
              fontWeight: activeTheme === 'all' ? 600 : 400,
            }}
          >
            All ({horizon.items.length})
          </button>
          {Object.entries(THEME_CONFIG).map(([id, t]) => {
            const count = horizon.items.filter((i) => i.theme === id).length;
            if (!count) return null;
            return (
              <button
                key={id}
                onClick={() => setActiveTheme(id)}
                style={{
                  padding: '0.25rem 0.75rem', borderRadius: '50px', fontSize: '0.78rem',
                  border: `1px solid ${activeTheme === id ? t.color : 'var(--border-light)'}`,
                  cursor: 'pointer',
                  background: activeTheme === id ? t.color + '20' : 'var(--bg-white)',
                  color: activeTheme === id ? t.color : 'var(--text-dark)',
                  fontWeight: activeTheme === id ? 600 : 400,
                }}
              >
                {t.icon} {t.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Feature cards */}
        {filteredItems.length === 0 ? (
          <p style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
            No items match this filter.
          </p>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '0.75rem',
            marginBottom: '2.5rem',
          }}>
            {filteredItems.map((item, i) => (
              <div key={i} style={{
                background: 'var(--bg-white)', border: '1px solid var(--border-light)',
                borderRadius: '10px', padding: '1rem 1.1rem',
                display: 'flex', flexDirection: 'column', gap: '0.5rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ fontSize: '1.1rem' }}>{item.icon}</span>
                    <strong style={{ fontSize: '0.875rem', color: 'var(--text-dark)' }}>{item.title}</strong>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-light)', lineHeight: 1.5 }}>
                  {item.body}
                </p>
                <div style={{ marginTop: 'auto', paddingTop: '0.25rem' }}>
                  <ThemeBadge themeId={item.theme} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chart: breakdown by theme */}
        <section style={{ marginBottom: '2.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-dark)', marginBottom: '0.35rem' }}>
            {horizon.label} horizon — focus breakdown
          </h2>
          <p style={{ color: 'var(--text-light)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Where the work is concentrated across strategic themes.
          </p>
          <div style={{
            background: 'var(--bg-white)', border: '1px solid var(--border-light)',
            borderRadius: '10px', padding: '1.25rem 1.5rem',
          }}>
            <ResponsiveContainer width="100%" height={chartData.length * 48 + 16}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 0, right: 24, bottom: 0, left: 8 }}
                barCategoryGap="30%"
              >
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: 'var(--text-light)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={160}
                  tick={{ fontSize: 12, fill: 'var(--text-dark)' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(label, i) => `${chartData[i]?.icon ?? ''} ${label}`}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--bg-light)' }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Recently shipped */}
        <section style={{ marginBottom: '2.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-dark)', marginBottom: '1rem' }}>
            Recently shipped
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem' }}>
            {SHIPPED.map((s, i) => (
              <div key={i} style={{
                background: 'var(--bg-white)', border: '1px solid var(--border-light)',
                borderRadius: '10px', padding: '1rem 1.1rem',
                display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
              }}>
                <span style={{
                  fontSize: '1.2rem',
                  background: 'var(--chip-green-bg, #dcfce7)',
                  borderRadius: '8px', padding: '0.3rem 0.4rem',
                  flexShrink: 0,
                }}>
                  {s.icon}
                </span>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: '0.875rem', color: 'var(--text-dark)' }}>{s.title}</strong>
                    <span style={{ fontSize: '0.72rem', color: 'var(--chip-green-text, #166534)', fontWeight: 600 }}>
                      ✓ {s.date}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.81rem', color: 'var(--text-light)', lineHeight: 1.5 }}>
                    {s.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Principles */}
        <section style={{ marginBottom: '2.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-dark)', marginBottom: '1rem' }}>
            How we build
          </h2>
          <div style={{ display: 'grid', gap: '0.65rem' }}>
            {PRINCIPLES.map((p, i) => (
              <div key={i} style={{
                display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
                background: 'var(--bg-white)', border: '1px solid var(--border-light)',
                borderRadius: '8px', padding: '0.75rem 1rem',
              }}>
                <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{p.icon}</span>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-dark)', lineHeight: 1.5 }}>{p.text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* What we don't do */}
        <section style={{ marginBottom: '2.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-dark)', marginBottom: '1rem' }}>
            What we don't do
          </h2>
          <div style={{
            background: 'var(--bg-white)', border: '1px solid var(--border-light)',
            borderRadius: '10px', padding: '1.25rem 1.5rem',
          }}>
            {[
              'No advertising',
              'No selling or sharing of family data with third parties',
              'No paywalling core tracking features',
              'No dark patterns around subscriptions',
            ].map((item, i, arr) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.4rem 0',
                borderBottom: i < arr.length - 1 ? '1px solid var(--border-light)' : 'none',
              }}>
                <span style={{ color: '#2e7d32', fontWeight: 700, flexShrink: 0 }}>✓</span>
                <span style={{ fontSize: '0.9rem', color: 'var(--text-dark)' }}>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="docs-cta-box">
          <p style={{ marginBottom: '1rem', color: 'var(--text-dark)' }}>
            Ready to try it? It's free to get started.
          </p>
          <button className="btn btn-primary" onClick={openSignup}>Create a free account</button>
        </div>

      </div>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container landing-footer-inner">
          <Logo variant="full" size={28} />
          <nav className="landing-footer-links">
            <a href="/docs">Help</a>
            <a href="/roadmap">Roadmap</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <button className="landing-footer-btn-link" onClick={openLogin}>Log in</button>
          </nav>
        </div>
      </footer>

      {authOpen && (
        <AuthModal
          initialMode={authMode}
          onClose={() => setAuthOpen(false)}
          onSuccess={() => { setAuthOpen(false); navigate('/app'); }}
        />
      )}
    </div>
  );
}
